import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import { logoutAndClearSite } from "../services/logout";
import { normalizeDisease, diagnosisLabel } from "../constants/diseaseOptions";
import { DISEASE_OPTIONS } from "../constants/diseaseOptions";

import { fetchStudies, updateStudy, createStudy } from "../services/studyService";
import { motion } from "framer-motion";
import { cardEntrance, AnimatedNumber } from "./motionPresets";
import { UI_REVAMP_ENABLED } from "../config/featureFlags";
import CrcNamesButton from "./study/CrcNamesButton";
import { greetingName, homeNameKey } from "../services/homeData";
import { getActiveSiteId, getActiveTenantId } from "../services/activeTenant";
import SidebarThemeToggle from "./SidebarThemeToggle";
import { Home as HomeNavIcon, Megaphone as AnnouncementsNavIcon } from "lucide-react";
import AddStudyModal from "./AddStudyModal";
import EditStudyModal from "./EditStudyModal";
import StudyClosePatientsModal from "./StudyClosePatientsModal";
import { isClosedStudy } from "../services/enrollmentRules";

import {
  Activity,
  Bell,
  LogOut,
  Search,
  Plus,
  BarChart3,
  FileText,
  Users,
  UserCog,
  Stethoscope,
  ClipboardList,
  ShieldCheck,
  Sparkles,
  Filter,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  Pencil,
  Microscope,
  HeartPulse,
  Workflow,
  Layers,
  Send,
  CalendarRange,
  Phone,
  Database,
  FileUp,
  ClipboardCheck,
} from "lucide-react";
import { PFT_IMPORT_ENABLED, PFT_UPLOAD_ENABLED } from "../config/featureFlags";
import { loadStudiesNeedingApproval } from "../services/studyReportApprovalService";

const STATUS_STYLES = {
  ACTIVE: {
    chip: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
    dot: "bg-emerald-400 shadow-[0_0_10px_2px_rgba(52,211,153,0.7)]",
    glow: "from-emerald-500/10 via-transparent to-transparent",
    bar: "from-emerald-400 to-teal-400",
  },
  "START-UP": {
    chip: "bg-amber-400/15 text-amber-300 border-amber-400/30",
    dot: "bg-amber-400 shadow-[0_0_10px_2px_rgba(251,191,36,0.7)]",
    glow: "from-amber-500/10 via-transparent to-transparent",
    bar: "from-amber-400 to-orange-400",
  },
  "ACTIVE/NR": {
    chip: "bg-sky-400/15 text-sky-300 border-sky-400/30",
    dot: "bg-sky-400 shadow-[0_0_10px_2px_rgba(56,189,248,0.7)]",
    glow: "from-sky-500/10 via-transparent to-transparent",
    bar: "from-sky-400 to-blue-400",
  },
  "CLOSED/CLOSING": {
    chip: "bg-slate-400/15 text-slate-300 border-slate-400/30",
    dot: "bg-slate-400",
    glow: "from-slate-500/5 via-transparent to-transparent",
    bar: "from-slate-400 to-slate-500",
  },
  DEFAULT: {
    chip: "bg-purple-400/15 text-purple-300 border-purple-400/30",
    dot: "bg-purple-400 shadow-[0_0_10px_2px_rgba(167,139,250,0.7)]",
    glow: "from-purple-500/10 via-transparent to-transparent",
    bar: "from-purple-400 to-indigo-400",
  },
};

const statusKey = (s) => (s || "").toString().trim().toUpperCase();

const Dashboard = () => {
  const [studies, setStudies] = useState([]);
  // Studies with weekly-report numbers waiting for an admin's OK: their cards turn amber.
  const [needsApproval, setNeedsApproval] = useState(() => new Set());
  const [editingStudy, setEditingStudy] = useState(null);
  // Set when a save closes a study, to offer its enrolled patients back to referrals.
  const [closedStudy, setClosedStudy] = useState(null);
  const location = useLocation();
  // UI revamp: the home sidebar's "Add Study" opens this page with its pop-up open.
  const [adding, setAdding] = useState(() => UI_REVAMP_ENABLED && !!location.state?.addStudy);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [diseaseFilter, setDiseaseFilter] = useState("All");
  const [isCommandDockOpen, setIsCommandDockOpen] = useState(true);
  const [mobileDockOpen, setMobileDockOpen] = useState(false);
  const navigate = useNavigate();
  // Consume that hand-off once, so a refresh doesn't reopen the pop-up.
  useEffect(() => {
    if (UI_REVAMP_ENABLED && location.state?.addStudy) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToVaultX = () => navigate("/ebinder");

  const nameMap = {
    "usinha@lunginnovations.com": "Utkarsh",
    "awitherow@lunginnovations.com": "Amber",
    "sbansal@lunginnovations.com": "Dr. Bansal",
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await logoutAndClearSite();
    navigate("/login");
  };

  const loadStudies = async () => {
    const data = (await fetchStudies()).map((s) => ({
      ...s,
      DISEASE: normalizeDisease(s.DISEASE),
    }));

    const statusRank = {
      ACTIVE: 1,
      "START-UP": 2,
      "ACTIVE/NR": 3,
      "CLOSED/CLOSING": 4,
    };

    const norm = (s) => (s || "").toString().trim().toUpperCase();

    const sorted = data.sort((a, b) => {
      const ra = statusRank[norm(a.STATUS)] ?? 99;
      const rb = statusRank[norm(b.STATUS)] ?? 99;
      if (ra !== rb) return ra - rb;
      return String(a.STUDY_ID || "").localeCompare(String(b.STUDY_ID || ""));
    });

    setStudies(sorted);
    // Fail-soft: without it the cards just aren't highlighted.
    loadStudiesNeedingApproval()
      .then(setNeedsApproval)
      .catch((err) => console.error("❌ Could not check studies for numbers to approve:", err));
  };

  useEffect(() => {
    loadStudies();
  }, []);

  const handleSave = async (updatedData) => {
    if (!updatedData.hasOwnProperty("COV")) {
      updatedData.COV = "";
    }
    await updateStudy(editingStudy.id, updatedData);
    const justClosed = !isClosedStudy(editingStudy) && isClosedStudy(updatedData);
    if (justClosed) setClosedStudy({ ...editingStudy, ...updatedData, id: editingStudy.id });
    setEditingStudy(null);
    loadStudies();
  };

  const handleCreate = async (newData) => {
    if (!newData.hasOwnProperty("COV")) {
      newData.COV = "";
    }
    await createStudy(newData);
    setAdding(false);
    loadStudies();
  };

  const uniqueStatuses = [
    "All",
    ...new Set(studies.map((s) => s.STATUS).filter(Boolean)),
  ];
  const uniqueDiseases = ["All", ...DISEASE_OPTIONS];

  const filteredStudies = studies.filter((study) => {
    const matchesSearch = `${study.STUDY_ID} ${study.NAME} ${study.SITE_ID}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "All" || study.STATUS === statusFilter;
    const matchesDisease =
      diseaseFilter === "All" || study.DISEASE === diseaseFilter;
    return matchesSearch && matchesStatus && matchesDisease;
  });

  // KPI tiles derived from current studies (read-only summary; filtering unchanged)
  const kpis = useMemo(() => {
    const total = studies.length;
    const active = studies.filter((s) => statusKey(s.STATUS) === "ACTIVE").length;
    const startup = studies.filter((s) => statusKey(s.STATUS) === "START-UP").length;
    const closed = studies.filter((s) => statusKey(s.STATUS) === "CLOSED/CLOSING").length;
    return { total, active, startup, closed };
  }, [studies]);

  const activeFilterCount =
    (searchTerm ? 1 : 0) +
    (statusFilter !== "All" ? 1 : 0) +
    (diseaseFilter !== "All" ? 1 : 0);

  // Action groups for the command dock — preserves every original navigation/handler
  const commandGroups = [
    {
      label: "Operations",
      icon: Workflow,
      actions: [
        // UI revamp: the personal home page, first in the sidebar.
        ...(UI_REVAMP_ENABLED
          ? [
              {
                label: "Home",
                icon: HomeNavIcon,
                onClick: () => navigate("/home"),
                tone: "from-purple-500/30 to-violet-500/30 border-purple-300/30 text-purple-100",
              },
              {
                label: "Announcements",
                icon: AnnouncementsNavIcon,
                onClick: () => navigate("/announcements"),
                tone: "from-amber-500/30 to-orange-500/30 border-amber-300/30 text-amber-100",
              },
            ]
          : []),
        {
          label: "Add Study",
          icon: Plus,
          onClick: () => setAdding(true),
          tone: "from-emerald-500/30 to-teal-500/30 border-emerald-300/30 text-emerald-100",
        },
        {
          label: "Statistics",
          icon: BarChart3,
          onClick: () => navigate("/statistics"),
          tone: "from-purple-500/30 to-indigo-500/30 border-purple-300/30 text-purple-100",
        },
        {
          label: "Weekly Reports",
          icon: FileText,
          onClick: () => navigate("/admin/weekly-reports"),
          tone: "from-indigo-500/30 to-blue-500/30 border-indigo-300/30 text-indigo-100",
        },
        {
          label: "Research Calendar",
          icon: CalendarRange,
          onClick: () => navigate("/research-calendar"),
          tone: "from-violet-500/30 to-fuchsia-500/30 border-violet-300/30 text-violet-100",
        },
      ],
    },
    {
      label: "Patient Flow",
      icon: HeartPulse,
      actions: [
        {
          label: "Enrolled / Potential",
          icon: ClipboardList,
          onClick: () => navigate("/admin/study-patients"),
          tone: "from-blue-500/30 to-cyan-500/30 border-blue-300/30 text-blue-100",
        },
        {
          label: "Referral Dashboard",
          icon: Stethoscope,
          onClick: () => navigate("/reception"),
          tone: "from-pink-500/30 to-fuchsia-500/30 border-pink-300/30 text-pink-100",
        },
        {
          label: "Coordinator",
          icon: UserCog,
          onClick: () => navigate("/coordinator"),
          tone: "from-amber-500/30 to-orange-500/30 border-amber-300/30 text-amber-100",
        },
        {
          label: "Mass Text Outreach",
          icon: Send,
          onClick: () => navigate("/referral"),
          tone: "from-teal-500/30 to-cyan-500/30 border-teal-300/30 text-teal-100",
        },
        {
          label: "Contacts",
          icon: Phone,
          onClick: () => navigate("/contacts"),
          tone: "from-amber-500/30 to-orange-500/30 border-amber-300/30 text-amber-100",
        },
        {
          label: "Raw Database",
          icon: Database,
          onClick: () => navigate("/coordinator/raw-database"),
          tone: "from-pink-500/30 to-rose-500/30 border-pink-300/30 text-pink-100",
        },
        ...(PFT_UPLOAD_ENABLED
          ? [
              {
                label: "Upload PFT PDFs",
                icon: FileUp,
                onClick: () => navigate("/admin/pft-upload"),
                tone: "from-cyan-500/30 to-sky-500/30 border-cyan-300/30 text-cyan-100",
              },
            ]
          : []),
        // Patients found in uploaded PFTs (the Coordinator page's "PFT Uploads").
        ...(PFT_IMPORT_ENABLED
          ? [
              {
                label: "Review PFT Patients",
                icon: Activity,
                onClick: () => navigate("/coordinator/pft-uploads", { state: { from: "dashboard" } }),
                tone: "from-emerald-500/30 to-teal-500/30 border-emerald-300/30 text-emerald-100",
              },
            ]
          : []),
      ],
    },
    {
      label: "Staff",
      icon: Users,
      actions: [
        {
          label: "Employee Credentials",
          icon: ShieldCheck,
          onClick: () => navigate("/credentials"),
          tone: "from-sky-500/30 to-blue-500/30 border-sky-300/30 text-sky-100",
        },
        {
          label: "Manage Coordinators",
          icon: UserCog,
          onClick: () => navigate("/admin/coordinators"),
          tone: "from-purple-500/30 to-indigo-500/30 border-purple-300/30 text-purple-100",
        },
        {
          label: "Monitors",
          icon: Microscope,
          onClick: () => navigate("/admin/monitors"),
          tone: "from-violet-500/30 to-purple-500/30 border-violet-300/30 text-violet-100",
        },
      ],
    },
    {
      label: "System",
      icon: Layers,
      actions: [
        {
          label: "Notifications",
          icon: Bell,
          onClick: () => navigate("/notifications"),
          tone: "from-rose-500/30 to-pink-500/30 border-rose-300/30 text-rose-100",
        },
        {
          label: "Log Out",
          icon: LogOut,
          onClick: handleLogout,
          tone: "from-red-500/30 to-rose-500/30 border-red-300/30 text-red-100",
        },
      ],
    },
  ];

  // UI revamp: the header greets you with the same name as your home page (your
  // account name, else what you entered in "What should we call you?", kept in
  // this browser per person + company + site). Read only; nothing is saved
  // here. With no name, the header reads as before.
  const homeName = (() => {
    if (!UI_REVAMP_ENABLED || !currentUser) return "";
    let typed = "";
    try {
      typed = localStorage.getItem(homeNameKey(currentUser.uid, getActiveTenantId(), getActiveSiteId())) || "";
    } catch {
      typed = "";
    }
    return greetingName({ authName: currentUser.displayName, typedName: typed });
  })();
  const displayName = homeName || (currentUser
    ? nameMap[currentUser.email] || "Staff"
    : "Staff");

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      {/* Full-page background image with layered gradients & glow */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <img
          src="/bg_ai.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
        <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
        <div className="absolute bottom-0 left-1/3 w-[480px] h-[480px] rounded-full bg-fuchsia-600/15 blur-[140px]" />
      </div>

      {/* Mobile drawer backdrop */}
      {mobileDockOpen && (
        <div
          onClick={() => setMobileDockOpen(false)}
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
        />
      )}

      <div className="flex min-h-screen">
        {/* ============ LEFT COMMAND DOCK SIDEBAR ============ */}
        <aside
          className={`fixed lg:sticky top-0 z-50 h-screen flex flex-col bg-slate-950/60 backdrop-blur-2xl border-r border-white/10 shadow-[8px_0_40px_-20px_rgba(124,58,237,0.45)] transition-all duration-300 ease-out ${
            isCommandDockOpen ? "w-64" : "w-[72px]"
          } ${
            mobileDockOpen
              ? "translate-x-0"
              : "-translate-x-full lg:translate-x-0"
          }`}
        >
          {/* Sidebar header — RegX logo + collapse toggle */}
          <div
            className={`flex p-3 border-b border-white/10 ${
              isCommandDockOpen
                ? "items-center gap-2 justify-between"
                : "flex-col items-center justify-center gap-2"
            }`}
          >
            <div className="relative shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
              {UI_REVAMP_ENABLED && isCommandDockOpen ? (
                // The logo says RegX, so it's shown full size with no text beside it.
                <img src="/RegX zoom.png" alt="RegX" className="relative h-12 w-auto max-w-[9rem] object-contain object-left" />
              ) : (
              <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center overflow-hidden p-1">
                <img
                  src="/RegX zoom.png"
                  alt="RegX"
                  className="w-full h-full object-contain scale-110"
                />
              </div>
              )}
            </div>
            {isCommandDockOpen && !UI_REVAMP_ENABLED && (
              <div className="flex-1 min-w-0">
                <div className="text-[9px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">
                  Lung Innovations
                </div>
                <div className="text-sm font-semibold text-white leading-tight tracking-tight truncate">
                  RegX Console
                </div>
              </div>
            )}
            <button
              onClick={() => setIsCommandDockOpen((o) => !o)}
              title={isCommandDockOpen ? "Collapse" : "Expand"}
              className="hidden lg:flex w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.1] hover:border-purple-300/30 items-center justify-center text-slate-300 hover:text-white transition shrink-0"
            >
              {isCommandDockOpen ? (
                <ChevronLeft className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
            </button>
          </div>

          {/* Sidebar groups */}
          <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 px-2 space-y-3 scrollbar-thin">
            {commandGroups.map((g) => {
              const GroupIcon = g.icon;
              return (
                <div key={g.label}>
                  {isCommandDockOpen ? (
                    <div className="px-1.5 mb-1.5 flex items-center gap-1.5">
                      {!UI_REVAMP_ENABLED && <GroupIcon className="w-3 h-3 text-purple-300" />}
                      <span className="text-[9px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
                        {g.label}
                      </span>
                      {!UI_REVAMP_ENABLED && (
                        <span className="ml-auto text-[9px] text-slate-500">
                          {g.actions.length}
                        </span>
                      )}
                    </div>
                  ) : UI_REVAMP_ENABLED ? (
                    // Collapsed: just space between sections, no icon.
                    <div className="h-2" aria-hidden="true" />
                  ) : (
                    <div
                      className="flex flex-col items-center gap-1 mb-1.5"
                      title={g.label}
                    >
                      <GroupIcon className="w-3 h-3 text-purple-300" />
                      <span className="block w-8 h-px bg-white/10" />
                    </div>
                  )}
                  <div className="space-y-0.5">
                    {g.actions.map((a) => {
                      const ActionIcon = a.icon;
                      return (
                        <button
                          key={a.label}
                          onClick={() => {
                            a.onClick();
                            setMobileDockOpen(false);
                          }}
                          title={a.label}
                          className={`group relative w-full flex items-center rounded-lg border border-transparent hover:border-white/10 hover:bg-white/[0.06] text-slate-200 hover:text-white transition-all ${
                            isCommandDockOpen
                              ? "gap-2.5 px-2 py-1.5 justify-start"
                              : "p-1.5 justify-center"
                          }`}
                        >
                          <span
                            className={`flex items-center justify-center w-7 h-7 rounded-md bg-gradient-to-br ${a.tone} border shrink-0 transition group-hover:scale-105 group-hover:shadow-[0_0_15px_-2px_rgba(139,92,246,0.5)]`}
                          >
                            <ActionIcon className="w-3.5 h-3.5" />
                          </span>
                          {isCommandDockOpen && (
                            <span className="text-xs font-medium truncate">
                              {a.label}
                            </span>
                          )}
                          {!isCommandDockOpen && (
                            <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded-md bg-slate-900/95 border border-white/10 text-[11px] text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-lg">
                              {a.label}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {UI_REVAMP_ENABLED && <SidebarThemeToggle expanded={isCommandDockOpen} />}
          </nav>

          {/* Sidebar footer — user pill. UI revamp: not shown (the header
              already says Welcome, with your name). */}
          {!UI_REVAMP_ENABLED && (
          <div
            className={`p-2.5 border-t border-white/10 ${
              isCommandDockOpen ? "" : "flex justify-center"
            }`}
          >
            {isCommandDockOpen ? (
              <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/[0.04] border border-white/10">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-[10px] font-bold ring-1 ring-white/20 shrink-0">
                  {displayName.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[9px] uppercase tracking-wider text-slate-400 leading-none">
                    Welcome
                  </div>
                  <div className="text-xs font-semibold text-white truncate">
                    {displayName}
                  </div>
                </div>
                <Sparkles className="w-3 h-3 text-purple-300 shrink-0" />
              </div>
            ) : (
              <div
                className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-[10px] font-bold ring-1 ring-white/20"
                title={displayName}
              >
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          )}
        </aside>

        {/* ============ RIGHT CONTENT COLUMN ============ */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Sticky top navigation */}
          <header className="sticky top-0 z-30 backdrop-blur-2xl bg-slate-950/40 border-b border-white/10 shadow-[0_8px_30px_-12px_rgba(124,58,237,0.35)]">
            <div className="px-3 sm:px-4 py-2.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  onClick={() => setMobileDockOpen(true)}
                  title="Open menu"
                  className="lg:hidden w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] flex items-center justify-center transition"
                >
                  <Menu className="w-4 h-4 text-slate-200" />
                </button>
                <div className="min-w-0">
                  {!UI_REVAMP_ENABLED && (
                    <div className="text-[9px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold">
                      Lung Innovations · RegX
                    </div>
                  )}
                  <h1 className="text-[15px] sm:text-base font-semibold tracking-tight leading-tight truncate">
                    {UI_REVAMP_ENABLED ? "Admin" : "Admin Command Center"}
                  </h1>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-md">
                  {!UI_REVAMP_ENABLED && <Sparkles className="w-3.5 h-3.5 text-purple-300" />}
                  <span className="text-xs text-slate-300">Welcome,</span>
                  <span className="text-xs font-semibold text-white">
                    {displayName}
                  </span>
                  <div className="ml-1 w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-[10px] font-bold ring-1 ring-white/20">
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                </div>

                <button
                  onClick={() => navigate("/notifications")}
                  title="Notifications"
                  className="relative w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 transition-all flex items-center justify-center group"
                >
                  <Bell className="w-4 h-4 text-slate-200 group-hover:text-white transition" />
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500 shadow-[0_0_8px_2px_rgba(244,63,94,0.7)] animate-pulse" />
                </button>

                <button
                  onClick={handleLogout}
                  title="Log out"
                  className="hidden sm:flex w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-red-500/15 hover:border-red-400/30 transition-all items-center justify-center group"
                >
                  <LogOut className="w-4 h-4 text-slate-200 group-hover:text-red-300 transition" />
                </button>
              </div>
            </div>
          </header>

          {/* Main content */}
          <main className="flex-1 px-3 sm:px-4 py-4 space-y-3">
            {/* KPI strip */}
            <section className="rx-kpi-strip grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          {[
            { label: "Total Studies", value: kpis.total, accent: "from-purple-500/20 to-indigo-500/20", icon: Layers, hoverBorder: "hover:border-purple-300/40" },
            { label: "Active", value: kpis.active, accent: "from-emerald-500/20 to-teal-500/20", icon: Activity, hoverBorder: "hover:border-emerald-300/40" },
            { label: "Start-Up", value: kpis.startup, accent: "from-amber-500/20 to-orange-500/20", icon: Sparkles, hoverBorder: "hover:border-amber-300/40" },
            { label: "Closed / Closing", value: kpis.closed, accent: "from-slate-500/20 to-slate-700/20", icon: ShieldCheck, hoverBorder: "hover:border-slate-300/30" },
          ].map((k, index) => (
            <motion.div
              {...cardEntrance(index)}
              key={k.label}
              className={`relative overflow-hidden rounded-2xl border border-white/10 ${k.hoverBorder} bg-white/[0.03] backdrop-blur-xl px-3.5 py-2.5 transition-all group hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-12px_rgba(139,92,246,0.4)]`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${k.accent} opacity-60 pointer-events-none`} />
              <div className="relative flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-300/80 font-medium">
                    {k.label}
                  </div>
                  <div className="text-2xl font-bold text-white tracking-tight leading-tight">
                    <AnimatedNumber value={k.value} />
                  </div>
                </div>
                <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:scale-105 transition">
                  <k.icon className="w-4 h-4 text-white/80" />
                </div>
              </div>
            </motion.div>
          ))}
        </section>

        {/* Filter bar */}
            <section className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl px-2.5 py-2 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.5)]">
          <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by Protocol, Name or Site ID…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-9 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="flex gap-2">
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none pl-3 pr-8 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition cursor-pointer"
                >
                  {uniqueStatuses.map((status) => (
                    <option key={status} value={status} className="bg-[#0b0820] text-slate-100">
                      {status === "All" ? "All Statuses" : status}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              </div>

              <div className="relative">
                <select
                  value={diseaseFilter}
                  onChange={(e) => setDiseaseFilter(e.target.value)}
                  className="appearance-none pl-3 pr-8 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition cursor-pointer"
                >
                  {uniqueDiseases.map((d) => (
                    <option key={d} value={d} className="bg-[#0b0820] text-slate-100">
                      {d === "All" ? "All Diseases" : diagnosisLabel(d)}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              </div>

              <div className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-xs text-slate-300">
                {!UI_REVAMP_ENABLED && <Filter className="w-3.5 h-3.5 text-purple-300" />}
                <span className="font-medium text-white">{filteredStudies.length}</span>
                <span className="text-slate-400">of {studies.length}</span>
                {activeFilterCount > 0 && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setStatusFilter("All");
                      setDiseaseFilter("All");
                    }}
                    className="ml-1 text-[10px] uppercase tracking-wider text-purple-300 hover:text-white transition"
                  >
                    Clear
                  </button>
                )}
              </div>
              {/* UI revamp: link the full names on studies (Lead CRC) to coordinators. */}
              {UI_REVAMP_ENABLED && <CrcNamesButton studies={studies} />}
            </div>
          </div>
        </section>

        {/* Study cards grid */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
          {filteredStudies.map((study, index) => {
            const key = statusKey(study.STATUS);
            const style = STATUS_STYLES[key] || STATUS_STYLES.DEFAULT;
            const goal = Number(study.GOAL) || 0;
            const enrolled = Number(study.ENROLLED) || 0;
            const progress =
              goal > 0 ? Math.min(100, Math.round((enrolled / goal) * 100)) : 0;
            const toApprove = needsApproval.has(study.id);

            return (
              <motion.div
                {...cardEntrance(index)}
                key={study.id}
                onClick={() => navigate(`/study/${study.id}`)}
                className={`group relative cursor-pointer rounded-2xl border backdrop-blur-xl overflow-hidden hover:-translate-y-0.5 transition-all duration-300 ${
                  toApprove
                    ? "study-needs-approval border-amber-400/60 bg-amber-500/[0.12] hover:border-amber-300/80 hover:shadow-[0_10px_40px_-10px_rgba(245,158,11,0.45)]"
                    : "border-white/10 bg-white/[0.035] hover:border-purple-300/30 hover:shadow-[0_10px_40px_-10px_rgba(139,92,246,0.45)]"
                }`}
              >
                {/* Status glow (amber when numbers wait for approval) */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${
                    toApprove ? "from-amber-500/20 via-amber-400/5 to-transparent" : style.glow
                  } opacity-80 pointer-events-none`}
                />
                {/* Top accent line */}
                <div
                  className={`absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r ${style.bar} opacity-70`}
                />

                <div className="relative p-3.5 flex flex-col gap-2.5">
                  {/* Badges row */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide border backdrop-blur-md ${style.chip}`}
                    >
                      {!UI_REVAMP_ENABLED && <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />}
                      {study.STATUS || "—"}
                    </span>
                    {study.DISEASE && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/[0.04] border border-white/10 text-slate-300">
                        {!UI_REVAMP_ENABLED && <HeartPulse className="w-2.5 h-2.5 text-purple-300" />}
                        {diagnosisLabel(study.DISEASE)}
                      </span>
                    )}
                    {toApprove && (
                      <span className="study-needs-approval-chip inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/15 border border-amber-400/50 text-amber-200">
                        {!UI_REVAMP_ENABLED && <ClipboardCheck className="w-2.5 h-2.5" />}
                        Numbers to approve
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingStudy(study);
                      }}
                      className="ml-auto w-6 h-6 rounded-md bg-white/[0.04] border border-white/10 hover:bg-purple-500/20 hover:border-purple-300/40 flex items-center justify-center text-slate-300 hover:text-white opacity-0 group-hover:opacity-100 transition-all"
                      title="Edit study"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  </div>

                  {/* Title */}
                  <div>
                    <div className="text-[10px] font-mono tracking-wider text-purple-300/80 uppercase">
                      {study.STUDY_ID || "—"}
                    </div>
                    <h3 className="text-base font-semibold text-white leading-snug line-clamp-2 group-hover:text-purple-100 transition">                      {study.NAME || "Untitled Study"}
                    </h3>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-3 gap-1.5 pt-0.5">
                    <div className="rounded-lg bg-white/[0.03] border border-white/5 px-2 py-1.5">
                      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-medium">
                        Goal
                      </div>
                      <div className="text-sm font-bold text-white leading-tight">
                        {study.GOAL || "—"}
                      </div>
                    </div>
                    <div className="rounded-lg bg-white/[0.03] border border-white/5 px-2 py-1.5">
                      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-medium">
                        Enrolled
                      </div>
                      <div className="text-sm font-bold text-emerald-300 leading-tight">
                        {study.ENROLLED || "—"}
                      </div>
                    </div>
                    <div className="rounded-lg bg-white/[0.03] border border-white/5 px-2 py-1.5">
                      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-medium">
                        SF
                      </div>
                      <div className="text-sm font-bold text-rose-300 leading-tight">
                        {study.SCREEN_FAILURES || "—"}
                      </div>
                    </div>
                  </div>

                  {/* Progress */}
                  {goal > 0 && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-slate-400 uppercase tracking-wider font-medium">
                          Enrollment
                        </span>
                        <span className="text-slate-200 font-semibold">
                          {progress}%
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${style.bar} shadow-[0_0_10px_rgba(139,92,246,0.5)] transition-all duration-500`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-1.5 border-t border-white/5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {!UI_REVAMP_ENABLED && (
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-[9px] font-bold text-white shrink-0">
                          {(study.LEAD_CRC || "—").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-[9px] uppercase tracking-wider text-slate-400 font-medium leading-none">
                          Lead CRC
                        </div>
                        <div className="text-[11px] text-slate-200 truncate">
                          {study.LEAD_CRC || "—"}
                        </div>
                      </div>
                    </div>
                    {study.SITE_ID && (
                      <span className="text-[10px] font-mono text-slate-400 px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/5">
                        {study.SITE_ID}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}

          {filteredStudies.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-white/10 bg-white/[0.02] backdrop-blur-xl p-10 text-center">
              <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-white/10 flex items-center justify-center mb-3">
                <Search className="w-5 h-5 text-purple-300" />
              </div>
              <h3 className="text-sm font-semibold text-white">No studies match</h3>
              <p className="text-xs text-slate-400 mt-1">
                Try clearing filters or adjusting your search.
              </p>
            </div>
          )}
        </section>

            <div className="h-8" />
          </main>
        </div>
      </div>

      {/* Floating mobile log-out (small-screen affordance) */}
      <button
        onClick={handleLogout}
        title="Log out"
        className="sm:hidden fixed bottom-4 right-4 z-30 w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-rose-600 shadow-[0_10px_30px_-5px_rgba(244,63,94,0.6)] flex items-center justify-center ring-1 ring-white/20 active:scale-95 transition"
      >
        <LogOut className="w-5 h-5 text-white" />
      </button>

      {/* Modals */}
      {editingStudy && (
        <EditStudyModal
          study={editingStudy}
          onSave={handleSave}
          onClose={() => setEditingStudy(null)}
        />
      )}
      {adding && (
        <AddStudyModal onSave={handleCreate} onClose={() => setAdding(false)} />
      )}
      <StudyClosePatientsModal
        open={!!closedStudy}
        study={closedStudy}
        onClose={() => setClosedStudy(null)}
      />
    </div>
  );
};

export default Dashboard;
